import { useSyncExternalStore } from "react";

import { invalidateCurrentOrderDetails } from "@/modules/cart/useCartDetails";
import { postOrdersCurrentLineitems } from "api/methods";
import type { PostRequest } from "api/types";

/**
 * Cart items an anonymous visitor asked to add before logging in. They live in
 * `sessionStorage` (same lifetime as the post-login return path) scoped to one
 * platform, and `appLayoutLoader` replays them into the real server cart on the
 * first authenticated layout load.
 */

const PENDING_CART_KEY = "anonymousBrowsing.pendingCart";

/**
 * The prices the visitor actually saw when they added the item, so `/cart` can
 * show the same numbers back to them while anonymous checkout is on. Absent on
 * items added while checkout was off (and on items stored before quotes
 * existed), where the cart stays price-free.
 */
/**
 * A special offer the visitor was shown against this item.
 *
 * `id` is the discount id, which is what the real cart reports back in its discount summary, so it is
 * what reconciliation matches on. The promo code is carried alongside because a special offer is not
 * applied by having its items in the cart — it has to be posted to the order, exactly as the signed-in
 * app does when a patient claims one. Both code fields are optional purely to tolerate a cart stored
 * in sessionStorage before this shipped; an entry without them simply cannot be applied.
 */
export type AdvertisedOffer = {
  id: string;
  title: string;
  promoCodeValue?: string;
  promoCodeId?: string;
};

export type PendingCartQuote = {
  unitPriceInCents: number;
  displayedPriceInCents: number;
  memberPriceInCents: number | null;
  advertisedDiscounts: AdvertisedOffer[];
  locationId: string;
};

export type PendingCartItem = {
  id: string;
  lineItem: PostRequest<"/orders/current/lineitems">;
  title: string;
  imageUrl?: string | null;
  quote?: PendingCartQuote;
  addedAt: string;
};

type PendingCart = { platformId: string; items: PendingCartItem[]; offers?: AdvertisedOffer[] };

const NO_OFFERS: AdvertisedOffer[] = [];

const NO_ITEMS: PendingCartItem[] = [];

const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

const isAdvertisedDiscount = (value: unknown): value is { id: string; title: string } =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { id: unknown }).id === "string" &&
  typeof (value as { title: unknown }).title === "string";

const isPendingCartQuote = (value: unknown): value is PendingCartQuote => {
  if (typeof value !== "object" || value === null) return false;
  const quote = value as PendingCartQuote;
  return (
    typeof quote.unitPriceInCents === "number" &&
    typeof quote.displayedPriceInCents === "number" &&
    (quote.memberPriceInCents === null || typeof quote.memberPriceInCents === "number") &&
    Array.isArray(quote.advertisedDiscounts) &&
    quote.advertisedDiscounts.every(isAdvertisedDiscount) &&
    typeof quote.locationId === "string"
  );
};

// A quote is optional: items stored before quotes existed, and items added
// while anonymous checkout was off, have none and must keep validating.
const isPendingCartItem = (value: unknown): value is PendingCartItem =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as PendingCartItem).id === "string" &&
  typeof (value as PendingCartItem).title === "string" &&
  typeof (value as PendingCartItem).lineItem === "object" &&
  (value as PendingCartItem).lineItem !== null &&
  ((value as PendingCartItem).quote === undefined ||
    isPendingCartQuote((value as PendingCartItem).quote));

// `useSyncExternalStore` needs a referentially stable snapshot, so the parsed
// cart is cached against the raw sessionStorage string.
let cachedRaw: string | null = null;
let cachedCart: PendingCart | null = null;

const readCart = (): PendingCart | null => {
  const raw = sessionStorage.getItem(PENDING_CART_KEY);
  if (raw === cachedRaw) return cachedCart;
  cachedRaw = raw;
  cachedCart = null;
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as PendingCart).platformId === "string" &&
      Array.isArray((parsed as PendingCart).items) &&
      (parsed as PendingCart).items.every(isPendingCartItem) &&
      ((parsed as PendingCart).offers === undefined ||
        (Array.isArray((parsed as PendingCart).offers) &&
          (parsed as PendingCart).offers!.every(isAdvertisedDiscount)))
    ) {
      cachedCart = parsed as PendingCart;
    }
  } catch {
    cachedCart = null;
  }
  return cachedCart;
};

const writeCart = (cart: PendingCart | null) => {
  // An offer with nothing to add — "10% off everything" — leaves the cart itemless but not empty,
  // so emptiness is measured across both or claiming such an offer would erase itself.
  const isEmpty = !cart || (cart.items.length === 0 && (cart.offers?.length ?? 0) === 0);
  if (isEmpty) sessionStorage.removeItem(PENDING_CART_KEY);
  else sessionStorage.setItem(PENDING_CART_KEY, JSON.stringify(cart));
  notify();
};

/** Items waiting for `platformId`; items stored for another platform are never returned. */
export const getPendingCartItems = (platformId: string): PendingCartItem[] => {
  const cart = readCart();
  return cart?.platformId === platformId ? cart.items : NO_ITEMS;
};

/** Adding on a different platform replaces the pending cart; a cart belongs to one platform. */
export const addPendingCartItem = (
  platformId: string,
  item: Omit<PendingCartItem, "id" | "addedAt">,
) => {
  writeCart({
    platformId,
    items: [
      ...getPendingCartItems(platformId),
      { ...item, id: crypto.randomUUID(), addedAt: new Date().toISOString() },
    ],
    offers: getPendingOffers(platformId),
  });
};

/** Offers the visitor claimed that had no items to choose; they apply to the cart as a whole. */
export const getPendingOffers = (platformId: string): AdvertisedOffer[] => {
  const cart = readCart();
  return cart?.platformId === platformId ? (cart.offers ?? NO_OFFERS) : NO_OFFERS;
};

/**
 * Claims an offer that has nothing to add — a percentage off everything, say. It is recorded against
 * the whole cart rather than an item, and claimed for real once the account exists.
 */
export const addPendingOffer = (platformId: string, offer: AdvertisedOffer) => {
  const existing = getPendingOffers(platformId);
  if (existing.some((claimed) => claimed.id === offer.id)) return;
  writeCart({
    platformId,
    items: getPendingCartItems(platformId),
    offers: [...existing, offer],
  });
};

export const removePendingCartItem = (platformId: string, id: string) => {
  writeCart({
    platformId,
    items: getPendingCartItems(platformId).filter((item) => item.id !== id),
    offers: getPendingOffers(platformId),
  });
};

/** `id` is the discount id the offer was claimed under, the same one `addPendingOffer` deduplicates on. */
export const removePendingOffer = (platformId: string, id: string) => {
  writeCart({
    platformId,
    items: getPendingCartItems(platformId),
    offers: getPendingOffers(platformId).filter((offer) => offer.id !== id),
  });
};

export const clearPendingCart = () => writeCart(null);

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const usePendingCartItems = (platformId: string | undefined) =>
  useSyncExternalStore(
    subscribe,
    () => (platformId ? getPendingCartItems(platformId) : NO_ITEMS),
    () => NO_ITEMS,
  );

export const usePendingOffers = (platformId: string | undefined) =>
  useSyncExternalStore(
    subscribe,
    () => (platformId ? getPendingOffers(platformId) : NO_OFFERS),
    () => NO_OFFERS,
  );

/** Both halves of the cart: items plus offers claimed with nothing to add. */
export const usePendingCartCount = (platformId: string | undefined) =>
  usePendingCartItems(platformId).length + usePendingOffers(platformId).length;

/** Whether the cart holds anything to pay for. A claimed offer discounts a purchase but is not one. */
export const hasPurchasableItems = (platformId: string) =>
  getPendingCartItems(platformId).length > 0;

export const useHasPurchasableItems = (platformId: string | undefined) =>
  useSyncExternalStore(
    subscribe,
    () => (platformId ? hasPurchasableItems(platformId) : false),
    () => false,
  );

/**
 * POSTs every item pending for `platformId` to the real cart, then empties the
 * store. Items stored for another platform are dropped without being posted.
 * Failed items are returned for reporting but not kept, so they cannot
 * resurface on a later session.
 */
export const replayPendingCart = async (
  platformId: string,
): Promise<{ added: number; failed: PendingCartItem[] }> => {
  const items = getPendingCartItems(platformId);
  const failed: PendingCartItem[] = [];
  let added = 0;

  for (const item of items) {
    try {
      await postOrdersCurrentLineitems(item.lineItem);
      added += 1;
    } catch (error) {
      console.error("[anonymousBrowsing] Failed to replay pending cart item:", error);
      failed.push(item);
    }
  }

  writeCart(null);

  if (added > 0) {
    await invalidateCurrentOrderDetails();
  }

  return { added, failed };
};
